import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { classNames } from '@egds/react-core/utils';
import styles from '../styles/egds-virtual-agent-control-loading-dots.css';
import styles$1 from '../styles/egds-virtual-agent-control-chat-container.css';
import React from 'react';
import { CSSTransition } from 'react-transition-group';
import styles$2 from '../styles/egds-virtual-agent-control-help-widget-container.css';
import styles$3 from '../styles/egds-virtual-agent-control-yelp-rating.css';

const getClassNameList = classNames.bind(styles);
const assignCssClasses = () => {
    const baseClass = 'loading-dots';
    return {
        wrapperCssClassName: getClassNameList(`${baseClass}-three_dots`),
        dotContainerClassName: getClassNameList(`${baseClass}-dot-container`),
        dotCssClassName: getClassNameList(`${baseClass}-dot`),
        middleDotCssClassName: getClassNameList(`${baseClass}-dot`, `${baseClass}-middle-dot`),
    };
};
const LoadingDots = () => {
    const cssClasses = assignCssClasses();
    return (jsx("div", { className: cssClasses.wrapperCssClassName, "data-test-id": "three-dots", children: jsxs("ul", { className: cssClasses.dotContainerClassName, children: [jsx("li", { className: cssClasses.dotCssClassName }), jsx("li", { className: cssClasses.middleDotCssClassName }), jsx("li", { className: cssClasses.dotCssClassName })] }) }));
};

const getClass$9 = classNames.bind(styles$1);
const assignClasses$9 = () => {
    const baseClass = 'chat-container';
    return {
        header: getClass$9(`${baseClass}-header`),
    };
};
const ChatContainerHeader = ({ children, ariaLabel = '', }) => {
    const classes = assignClasses$9();
    return (jsx("div", { className: classes.header, role: "banner", "aria-label": ariaLabel, children: children }));
};
ChatContainerHeader.displayName = 'ChatContainerHeader';

const CHAT_CONTAINER_CONTENT_ID = 'chat-container-content';
/* istanbul ignore next */
const ScrollChatContentToBottomEvent = typeof window !== 'undefined' && typeof window.CustomEvent === 'function'
    ? new CustomEvent(`scroll_${CHAT_CONTAINER_CONTENT_ID}`)
    : { type: `scroll_${CHAT_CONTAINER_CONTENT_ID}` };

const defineLayout$1 = (children) => {
    const layout = {
        filteredChildren: [],
        header: null,
        content: null,
        footer: null,
    };
    React.Children.toArray(children).forEach((child) => {
        if (child.type?.displayName === 'ChatContainerHeader') {
            layout.header = child;
        }
        else if (child.type?.displayName === 'ChatContainerContent') {
            layout.content = child;
            if (child.props?.children) {
                const contentChildren = React.Children.toArray(child.props.children);
                layout.filteredChildren = [...layout.filteredChildren, ...contentChildren];
            }
        }
        else if (child.type?.displayName === 'ChatContainerFooter') {
            layout.footer = child;
        }
        else {
            layout.filteredChildren = [...layout.filteredChildren, child];
        }
    });
    return layout;
};
const scrollChatContent = (e) => {
    e.stopPropagation();
    const chatContentElement = document.getElementById(CHAT_CONTAINER_CONTENT_ID);
    /* istanbul ignore else */
    if (chatContentElement) {
        chatContentElement.scrollTop = chatContentElement.scrollHeight;
    }
};

const getClass$8 = classNames.bind(styles$1);
const assignClasses$8 = () => {
    const baseClass = 'chat-container';
    return {
        content: getClass$8(`${baseClass}-content`),
        contentPadded: getClass$8(`${baseClass}-content-padded`),
    };
};
const ChatContainerContent = ({ children, padded, ariaLabel = '', }) => {
    React.useEffect(() => {
        document.addEventListener(ScrollChatContentToBottomEvent.type, scrollChatContent);
        return () => document.removeEventListener(ScrollChatContentToBottomEvent.type, scrollChatContent);
    }, []);
    const classes = assignClasses$8();
    return (jsx("div", { id: CHAT_CONTAINER_CONTENT_ID, className: padded ? classes.contentPadded : classes.content, role: "main", "aria-label": ariaLabel, children: children }));
};
ChatContainerContent.displayName = 'ChatContainerContent';

const getClass$7 = classNames.bind(styles$1);
const assignClasses$7 = () => {
    const baseClass = 'chat-container';
    return {
        footer: getClass$7(`${baseClass}-footer`),
    };
};
const ChatContainerFooter = ({ children, ariaLabel = '', }) => {
    const classes = assignClasses$7();
    return (jsx("div", { className: classes.footer, role: "complementary", "aria-label": ariaLabel, children: children }));
};
ChatContainerFooter.displayName = 'ChatContainerFooter';

const getClass$6 = classNames.bind(styles$1);
const assignClasses$6 = () => {
    const baseClass = 'chat-container';
    const baseClassSheet = `${baseClass}-sheet`;
    return {
        sheet: getClass$6(`${baseClassSheet}`),
        embedded: getClass$6(`${baseClass}-embedded`),
        transition: {
            enter: getClass$6(`${baseClassSheet}-enter`),
            enterActive: getClass$6(`${baseClassSheet}-enter-active`),
            enterDone: getClass$6(`${baseClassSheet}-enter-done`),
            exit: getClass$6(`${baseClassSheet}-exit`),
            exitActive: getClass$6(`${baseClassSheet}-exit-active`),
            exitDone: getClass$6(`${baseClassSheet}-exit-done`),
        },
    };
};
const ChatContainer = ({ children, embedded, padded, visible, }) => {
    const classes = assignClasses$6();
    const { filteredChildren, header, content, footer } = defineLayout$1(children);
    return (jsx(Fragment, { children: jsx(CSSTransition, { classNames: classes.transition, in: visible || embedded, mountOnEnter: true, timeout: 300, children: jsxs("section", { className: embedded ? classes.embedded : classes.sheet, children: [header, content ? (React.cloneElement(content, { padded }, ...filteredChildren)) : (jsx(ChatContainerContent, { padded: padded, children: filteredChildren })), footer] }) }) }));
};
ChatContainer.defaultProps = {
    embedded: false,
    padded: false,
};
ChatContainer.displayName = 'ChatContainer';

const getClass$5 = classNames.bind(styles$2);
const assignClasses$5 = () => {
    const baseClass = 'help-widget-container';
    return {
        header: getClass$5(`${baseClass}-header`),
    };
};
const HelpWidgetContainerHeader = ({ children, ariaLabel = '', }) => {
    const classes = assignClasses$5();
    return (jsx("div", { className: classes.header, role: "banner", "aria-label": ariaLabel, children: children }));
};
HelpWidgetContainerHeader.displayName = 'HelpWidgetContainerHeader';

const getClass$4 = classNames.bind(styles$2);
const assignClasses$4 = () => {
    const baseClass = 'help-widget-container';
    return {
        content: getClass$4(`${baseClass}-content`),
        contentPadded: getClass$4(`${baseClass}-content-padded`),
    };
};
const HelpWidgetContainerContent = ({ children, padded, ariaLabel = '', }) => {
    const classes = assignClasses$4();
    return (jsx("div", { id: "help-widget-container-content", className: padded ? classes.contentPadded : classes.content, role: "main", "aria-label": ariaLabel, children: children }));
};
HelpWidgetContainerContent.displayName = 'HelpWidgetContainerContent';

const getClass$3 = classNames.bind(styles$2);
const assignClasses$3 = () => {
    const baseClass = 'help-widget-container';
    return {
        footer: getClass$3(`${baseClass}-footer`),
    };
};
const HelpWidgetContainerFooter = ({ children, ariaLabel = '', }) => {
    const classes = assignClasses$3();
    return (jsx("div", { className: classes.footer, role: "complementary", "aria-label": ariaLabel, children: children }));
};
HelpWidgetContainerFooter.displayName = 'HelpWidgetContainerFooter';

const defineLayout = (children) => {
    const layout = {
        filteredChildren: [],
        header: null,
        content: null,
        footer: null,
    };
    React.Children.toArray(children).forEach((child) => {
        if (child.type?.displayName === 'HelpWidgetContainerHeader') {
            layout.header = child;
        }
        else if (child.type?.displayName === 'HelpWidgetContainerContent') {
            layout.content = child;
        }
        else if (child.type?.displayName === 'HelpWidgetContainerFooter') {
            layout.footer = child;
        }
        else {
            layout.filteredChildren = [...layout.filteredChildren, child];
        }
    });
    return layout;
};

const getClass$2 = classNames.bind(styles$2);
const assignClasses$2 = () => {
    const baseClass = 'help-widget-container';
    const baseClassSheet = `${baseClass}-sheet`;
    return {
        sheet: getClass$2(`${baseClassSheet}`),
        embedded: getClass$2(`${baseClass}-embedded`),
        transition: {
            enter: getClass$2(`${baseClassSheet}-enter`),
            enterActive: getClass$2(`${baseClassSheet}-enter-active`),
            enterDone: getClass$2(`${baseClassSheet}-enter-done`),
            exit: getClass$2(`${baseClassSheet}-exit`),
            exitActive: getClass$2(`${baseClassSheet}-exit-active`),
            exitDone: getClass$2(`${baseClassSheet}-exit-done`),
        },
    };
};
const HelpWidgetContainer = ({ children, embedded, padded, visible, }) => {
    const classes = assignClasses$2();
    const { filteredChildren, header, content, footer } = defineLayout(children);
    return (jsx(Fragment, { children: jsx(CSSTransition, { classNames: classes.transition, in: visible || embedded, mountOnEnter: true, timeout: 300, children: jsxs("section", { className: embedded ? classes.embedded : classes.sheet, children: [header, content ? (React.cloneElement(content, { padded }, ...filteredChildren)) : (jsx(HelpWidgetContainerContent, { padded: padded, children: filteredChildren })), footer] }) }) }));
};
HelpWidgetContainer.defaultProps = {
    embedded: false,
    padded: false,
};
HelpWidgetContainer.displayName = 'HelpWidgetContainer';

const getClass$1 = classNames.bind(styles$3);
const assignClasses$1 = () => {
    const baseClass = 'yelp-rating';
    return {
        star: getClass$1(`${baseClass}__star`),
    };
};
const Colors = {
    'star-color-grey': 'rgb(187,186,192)',
};
const YelpStarSVG = ({ half = false, fill, empty = false, }) => {
    const css = assignClasses$1();
    return (jsxs("svg", { className: css.star, width: "20", height: "20", viewBox: "0 0 20 20", children: [jsx("path", { fill: empty ? Colors['star-color-grey'] : fill, opacity: "1", d: "M0 4C0 1.79086 1.79086 0 4 0H10V20H4C1.79086 20 0 18.2091 0 16V4Z" }), jsx("path", { fill: half || empty ? Colors['star-color-grey'] : fill, opacity: "1", d: "M20 4C20 1.79086 18.2091 0 16 0H10V20H16C18.2091 20 20 18.2091 20 16V4Z" }), jsx("path", { fill: "white", fillRule: "evenodd", clipRule: "evenodd", d: "M10 13.3736L12.5949 14.7111C12.7378 14.7848 12.9006 14.8106 13.0593 14.7847C13.4681 14.718 13.7454 14.3325 13.6787 13.9237L13.2085 11.0425L15.2824 8.98796C15.3967 8.8748 15.4715 8.72792 15.4959 8.569C15.5588 8.15958 15.2779 7.77672 14.8685 7.71384L11.983 7.2707L10.6699 4.66338C10.5975 4.51978 10.481 4.40322 10.3374 4.33089C9.96742 4.14458 9.51648 4.29344 9.33017 4.66338L8.01705 7.2707L5.13157 7.71384C4.97265 7.73825 4.82577 7.81309 4.71261 7.92731C4.42109 8.22158 4.42332 8.69645 4.71759 8.98796L6.79152 11.0425L6.32131 13.9237C6.29541 14.0824 6.3212 14.2452 6.39486 14.3881C6.58464 14.7563 7.03696 14.9009 7.40514 14.7111L10 13.3736Z" })] }));
};
YelpStarSVG.displayName = 'YelpStarSVG';

const getClass = classNames.bind(styles$3);
const assignClasses = () => {
    const baseClass = 'yelp-rating';
    return {
        yelpRating: getClass(`${baseClass}`),
        star: getClass(`${baseClass}__star`),
    };
};
const ColorMap = {
    'star-color-red': 'rgb(251, 67, 60)',
    'star-color-scarlet': 'rgb(255, 100, 61)',
    'star-color-orange': 'rgb(255, 135, 66)',
    'star-color-amber': 'rgb(255, 173, 72)',
    'star-color-yellow': 'rgb(255, 204, 75)',
};
const RATING_LOWER_BOUND = 0.0;
const RATING_UPPER_BOUND = 5.0;
const STAR_REMAINDER_LOWER_BOUND = 0.2;
const STAR_REMAINDER_UPPER_BOUND = 0.8;
const YelpRating = ({ rating }) => {
    const css = assignClasses();
    const preciseRating = parseFloat(rating.toFixed(1));
    const remainder = parseFloat((preciseRating - Math.floor(preciseRating)).toFixed(1));
    // Take the floor of the rating value and place it within bounds - we add a star if the the remainder is greater than or equal to the remainder upper bound to support rounding up
    const filledStars = Math.max(RATING_LOWER_BOUND, Math.min(Math.floor(preciseRating), RATING_UPPER_BOUND)) +
        (remainder >= STAR_REMAINDER_UPPER_BOUND && preciseRating > RATING_LOWER_BOUND ? 1 : 0);
    const halfStar = preciseRating < RATING_UPPER_BOUND &&
        preciseRating > RATING_LOWER_BOUND &&
        remainder > STAR_REMAINDER_LOWER_BOUND &&
        remainder < STAR_REMAINDER_UPPER_BOUND;
    const emptyStars = RATING_UPPER_BOUND - filledStars - (halfStar ? 1 : 0);
    const getFillColorByRating = (floatRating) => {
        if (floatRating >= 4.0 + STAR_REMAINDER_UPPER_BOUND)
            return ColorMap['star-color-red'];
        if (floatRating >= 3.0 + STAR_REMAINDER_UPPER_BOUND)
            return ColorMap['star-color-scarlet'];
        if (floatRating >= 2.0 + STAR_REMAINDER_UPPER_BOUND)
            return ColorMap['star-color-orange'];
        if (floatRating >= 1.0 + STAR_REMAINDER_UPPER_BOUND)
            return ColorMap['star-color-amber'];
        return ColorMap['star-color-yellow'];
    };
    const fillColor = getFillColorByRating(preciseRating);
    return (jsxs("div", { className: css.yelpRating, "aria-describedby": `a yelp branded rating with ${preciseRating} stars`, children: [[...Array(filledStars)].map((_, i) => (jsx(YelpStarSVG, { fill: fillColor }, `filled-star-${i}`))), halfStar ? jsx(YelpStarSVG, { fill: fillColor, half: true }) : null, emptyStars >= 0 && [...Array(emptyStars)].map((_, i) => jsx(YelpStarSVG, { empty: true }, `filled-star-${i}`))] }));
};
YelpRating.displayName = 'YelpRating';

export { ChatContainer, ChatContainerContent, ChatContainerFooter, ChatContainerHeader, HelpWidgetContainer, HelpWidgetContainerContent, HelpWidgetContainerFooter, HelpWidgetContainerHeader, LoadingDots, ScrollChatContentToBottomEvent, YelpRating };
//# sourceMappingURL=index.esm.js.map
